import { useState, useEffect, useMemo } from "react";
import { WEBINAR_DATES_MS, WEBINAR_DATES_ZS } from "../data/mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ArrowRight, Video, CalendarDays, MessageCircleQuestion, Check, Loader2, Clock, School, GraduationCap } from "lucide-react";
import axios from "axios";
import { getTrackedOperatorId } from "../utils/operatorTracking";
import { SchoolLookup } from "./SchoolLookup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fetch dates from API, fallback to hardcoded
const useFetchDates = () => {
  const [msDates, setMsDates] = useState(WEBINAR_DATES_MS);
  const [zsDates, setZsDates] = useState(WEBINAR_DATES_ZS);
  useEffect(() => {
    axios.get(`${API}/webinar-dates`).then(res => {
      if (Array.isArray(res.data) && res.data.length > 0) {
        setMsDates(res.data.filter(d => d.school_type === "ms"));
        setZsDates(res.data.filter(d => d.school_type === "zs"));
      }
    }).catch(() => {});
  }, []);
  return { msDates, zsDates };
};

// Parse date as Czech local time - NEVER as UTC
const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date(0);
  // Strip Z, +XX:XX or any timezone suffix - treat as local
  const clean = dateStr.replace("Z", "").replace(/[+-]\d{2}:\d{2}$/, "");
  // new Date("YYYY-MM-DDTHH:MM:SS") without Z = local time in most browsers
  // But to be 100% safe, parse manually
  const [datePart, timePart] = clean.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [h, min, s] = (timePart || "00:00:00").split(":").map(Number);
  return new Date(y, m - 1, d, h, min, s || 0);
};

const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({});
  useEffect(() => {
    const calc = () => {
      const diff = parseLocalDate(targetDate) - new Date();
      if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
      return { days: Math.floor(diff / 86400000), hours: Math.floor((diff / 3600000) % 24), minutes: Math.floor((diff / 60000) % 60), seconds: Math.floor((diff / 1000) % 60), expired: false };
    };
    setTimeLeft(calc());
    const i = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(i);
  }, [targetDate]);
  if (timeLeft.expired) return <span className="text-xs text-blue-200/40">Proběhla</span>;
  return (
    <div className="flex items-center gap-1.5">
      {timeLeft.days > 0 && <><span className="text-sm font-bold text-[#FFC72C]">{timeLeft.days}</span><span className="text-[10px] text-blue-200/40 mr-0.5">d</span></>}
      <span className="text-sm font-bold text-[#FFC72C]">{String(timeLeft.hours).padStart(2,'0')}</span><span className="text-[10px] text-blue-200/40">h</span>
      <span className="text-[#FFC72C]/40 text-xs">:</span>
      <span className="text-sm font-bold text-[#FFC72C]">{String(timeLeft.minutes).padStart(2,'0')}</span><span className="text-[10px] text-blue-200/40">m</span>
      <span className="text-[#FFC72C]/40 text-xs">:</span>
      <span className="text-sm font-bold text-[#FFC72C]">{String(timeLeft.seconds).padStart(2,'0')}</span><span className="text-[10px] text-blue-200/40">s</span>
    </div>
  );
};

export const WebinarSection = ({ onOrderClick, schoolType: initialType }) => {
  const [selectedType, setSelectedType] = useState(initialType || "ms");
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", ico: "", school_name: "", webinar_date: "", school_type: initialType || "ms" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const { msDates, zsDates } = useFetchDates();

  useEffect(() => {
    setFormData(prev => ({ ...prev, school_type: selectedType, webinar_date: "" }));
  }, [selectedType]);

  const typeDates = selectedType === "zs" ? zsDates : msDates;
  const upcomingTypeDates = useMemo(() => {
    const now = new Date();
    return typeDates.filter(w => parseLocalDate(w.date) > now);
  }, [typeDates]);
  const noDates = upcomingTypeDates.length === 0;

  const allUpcomingDates = useMemo(() => {
    const now = new Date();
    const ms = msDates.filter(w => parseLocalDate(w.date) > now).map(w => ({ ...w, type: "ms" }));
    const zs = zsDates.filter(w => parseLocalDate(w.date) > now).map(w => ({ ...w, type: "zs" }));
    return [...ms, ...zs].sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [msDates, zsDates]);
  const nextDate = allUpcomingDates[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.ico.trim()) { setError("Prosím vyplňte všechna pole a vyberte školu."); return; }
    if (!formData.email.includes("@")) { setError("Prosím zadejte platný email."); return; }
    if (!noDates && !formData.webinar_date) { setError("Prosím vyberte termín živé ukázky."); return; }
    setLoading(true);
    try {
      const trackedOp = getTrackedOperatorId();
      const submitData = { ...formData };
      if (trackedOp) submitData.operator_id = trackedOp;
      await axios.post(`${API}/webinar-register`, submitData);
      setSuccess(true);
    } catch { setError("Něco se nepovedlo. Zkuste to prosím znovu."); }
    finally { setLoading(false); }
  };

  return (
    <section className="relative py-16 lg:py-20 overflow-hidden">
      <div className="absolute inset-0 bg-white" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div id="webinar" className="relative bg-gradient-to-br from-[#0049B3] via-[#003A8C] to-[#002766] rounded-[2rem] p-8 lg:p-12 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFC72C]/6 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#87CEEB]/6 rounded-full blur-3xl" />

          <div className="relative grid lg:grid-cols-2 gap-10 items-start">
            {/* Left */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FFC72C]/15 border border-[#FFC72C]/25 rounded-full px-4 py-2 mb-6">
                <Video className="w-4 h-4 text-[#FFC72C]" />
                <span className="text-sm font-bold text-[#FFC72C]">Živé ukázky — každý týden</span>
              </div>

              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: '1.15' }}>
                Chcete vidět Agenta ŠVP<span className="text-[#FFC72C]"> v akci?</span>
              </h3>

              <p className="text-blue-100/70 text-base leading-relaxed mb-6">
                Přijďte na živou ukázku. Bez teorie a obecných řečí — ukážeme vám přímo v aplikaci, jak vzniká nový ŠVP krok za krokem.
              </p>

              {/* School type toggle */}
              <div className="mb-6">
                <p className="text-xs font-semibold text-blue-200/50 uppercase tracking-wider mb-3">Zajímám se o ukázku pro:</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setSelectedType("zs")}
                    className={`flex items-center gap-2.5 px-5 py-3 rounded-xl font-bold text-sm transition-all duration-300 ${
                      selectedType === "zs"
                        ? "bg-[#FFC72C] text-[#1A1A2E] shadow-lg shadow-amber-500/20"
                        : "bg-white/10 text-white/60 border border-white/10 hover:bg-white/15"
                    }`}
                  >
                    <GraduationCap className="w-5 h-5" />
                    Základní školy
                  </button>
                  <button
                    onClick={() => setSelectedType("ms")}
                    className={`flex items-center gap-2.5 px-5 py-3 rounded-xl font-bold text-sm transition-all duration-300 ${
                      selectedType === "ms"
                        ? "bg-[#FFC72C] text-[#1A1A2E] shadow-lg shadow-amber-500/20"
                        : "bg-white/10 text-white/60 border border-white/10 hover:bg-white/15"
                    }`}
                  >
                    <School className="w-5 h-5" />
                    Mateřské školy
                  </button>
                </div>
              </div>

              {/* Dates */}
              {allUpcomingDates.length === 0 ? (
                <div className="bg-white/5 border border-white/10 rounded-xl p-5 mb-6 text-center">
                  <p className="text-blue-100/60 text-sm">Žádné nadcházející termíny.</p>
                </div>
              ) : (
                <div className="mb-6">
                  <h4 className="text-xs font-bold text-[#FFC72C] uppercase tracking-wider mb-3">Nejbližší termíny</h4>
                  <div className="space-y-2">
                    {allUpcomingDates.slice(0, 6).map((w, idx) => {
                      const isNext = nextDate && w.date === nextDate.date && w.type === nextDate.type;
                      const isZS = w.type === "zs";
                      return (
                        <div key={`${w.type}-${idx}`} className={`flex items-center justify-between rounded-xl px-4 py-3 border ${isNext ? "bg-[#FFC72C]/10 border-[#FFC72C]/25" : "bg-white/5 border-white/5"}`}>
                          <div className="flex items-center gap-3">
                            <CalendarDays className={`w-4 h-4 ${isNext ? "text-[#FFC72C]" : "text-blue-200/40"}`} />
                            <span className={`text-sm font-bold ${isNext ? "text-white" : "text-blue-100/70"}`}>{w.dayLabel}</span>
                            <span className={`text-sm ${isNext ? "text-blue-100/80" : "text-blue-200/40"}`}>{w.timeLabel}</span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isZS ? "bg-amber-500/20 text-amber-300" : "bg-blue-400/20 text-blue-300"}`}>
                              {isZS ? "ZŠ" : "MŠ"}
                            </span>
                            {isNext && <span className="text-[10px] font-bold text-[#FFC72C] bg-[#FFC72C]/15 px-2 py-0.5 rounded-full">NEJBLIŽŠÍ</span>}
                          </div>
                          <CountdownTimer targetDate={w.date} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Info cards */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: Video, title: "Živá ukázka", desc: "Přímo v aplikaci" },
                  { icon: MessageCircleQuestion, title: "Vaše dotazy", desc: "Odpovíme na vše" },
                  { icon: CalendarDays, title: "Každý týden", desc: "Nové termíny pravidelně" }
                ].map((item) => (
                  <div key={item.title} className="bg-white/5 rounded-xl p-3 border border-white/5">
                    <item.icon className="w-5 h-5 text-[#FFC72C] mb-2" />
                    <div className="text-sm font-bold text-white">{item.title}</div>
                    <div className="text-[11px] text-blue-200/40 mt-0.5">{item.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 lg:p-8 border border-white/10">
              {success ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7 text-emerald-400" /></div>
                  <h4 className="text-lg font-bold text-white mb-2">Registrace úspěšná!</h4>
                  <p className="text-blue-200/60 text-sm mb-5">Brzy vám pošleme email s odkazem na nejbližší živou ukázku.</p>
                  <Button className="bg-white/10 hover:bg-white/15 text-white rounded-xl font-semibold px-5 border border-white/10" onClick={() => { setSuccess(false); setFormData({ name: "", email: "", phone: "", ico: "", school_name: "", webinar_date: "", school_type: selectedType }); }}>
                    Přihlásit dalšího kolegu
                  </Button>
                </div>
              ) : (
                <>
                  <h4 className="text-lg font-bold text-white mb-1">Přihlaste se na živou ukázku</h4>
                  <p className="text-blue-200/50 text-sm mb-5">
                    {selectedType === "zs" ? "Ukázka pro základní školy" : "Ukázka pro mateřské školy"} — pošleme vám odkaz
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <Input placeholder="Vaše jméno" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-white/10 border-white/10 text-white placeholder:text-blue-200/30 rounded-xl py-5 focus:border-[#FFC72C]/50 focus:ring-[#FFC72C]/20" />
                    <Input type="email" placeholder="Váš email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-white/10 border-white/10 text-white placeholder:text-blue-200/30 rounded-xl py-5 focus:border-[#FFC72C]/50 focus:ring-[#FFC72C]/20" />
                    <Input type="tel" placeholder="Telefon" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-white/10 border-white/10 text-white placeholder:text-blue-200/30 rounded-xl py-5 focus:border-[#FFC72C]/50 focus:ring-[#FFC72C]/20" />
                    <div>
                      <span className="text-xs font-semibold text-blue-200/50 mb-1 block">Škola (hledejte podle IČO nebo názvu)</span>
                      <SchoolLookup darkMode onSelect={({ ico, school_name }) => setFormData({ ...formData, ico, school_name })} />
                    </div>

                    {noDates ? (
                      <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                        <p className="text-blue-100/50 text-sm">Termíny pro {selectedType === "zs" ? "ZŠ" : "MŠ"} budou brzy vyhlášeny.</p>
                        <p className="text-blue-200/30 text-xs mt-1">Přihlaste se a dáme vám vědět.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <span className="text-xs font-semibold text-blue-200/50">Vyberte termín</span>
                        <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
                          {upcomingTypeDates.map((w, idx) => (
                            <button key={idx} type="button" onClick={() => setFormData({ ...formData, webinar_date: w.date })}
                              className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 border text-left transition-all duration-200 ${formData.webinar_date === w.date ? "bg-[#FFC72C]/15 border-[#FFC72C]/40 text-white" : "bg-white/5 border-white/10 text-blue-100/60 hover:bg-white/10"}`}>
                              <CalendarDays className={`w-4 h-4 flex-shrink-0 ${formData.webinar_date === w.date ? "text-[#FFC72C]" : "text-blue-200/30"}`} />
                              <span className="text-sm font-semibold">{w.dayLabel}</span>
                              <span className="text-sm">{w.timeLabel}</span>
                              {formData.webinar_date === w.date && <Check className="w-4 h-4 text-[#FFC72C] ml-auto" />}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {error && <p className="text-sm text-red-300 font-medium">{error}</p>}

                    <Button type="submit" disabled={loading} size="lg" className="w-full bg-[#FFC72C] hover:bg-[#E5B326] text-[#1A1A2E] rounded-xl font-bold py-5 text-base shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 transition-all duration-300 disabled:opacity-50">
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>Přihlásit se na živou ukázku<ArrowRight className="ml-2 w-5 h-5" /></>)}
                    </Button>
                    <p className="text-[11px] text-center text-blue-200/25">Žádný spam — pouze pozvánky na živé ukázky.</p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
